import { AxiosResponse } from 'axios';
import HttpClient from '../utils/HttpClientHandler';
import { ClientFeatureConfig, FeatureInterfaceBase, NewClientFeatureConfig } from '../types/Feature';

const ClientFeatureConfigService = class ClientFeatureConfigService {
    #baseURL: string;

    constructor() {
        this.#baseURL = '/dashboard/client';
    }

    public async getAllFeatureConfigByClientId(
        clientId: number,
        featureName?: string,
    ): Promise<ClientFeatureConfig<FeatureInterfaceBase>[]> {
        const response: AxiosResponse = await HttpClient.get(`${this.#baseURL}/${clientId}/featureconfig`, {
            params: featureName ? { name: featureName } : {},
        });
        const { data }: { data: ClientFeatureConfig<FeatureInterfaceBase>[] } = response;
        return data;
    }

    public async getClientFeatureConfigByFeatureId<T extends FeatureInterfaceBase>(
        clientId: number,
        featureId: number,
    ): Promise<ClientFeatureConfig<T>> {
        const response: AxiosResponse = await HttpClient.get(`${this.#baseURL}/${clientId}/featureconfig/${featureId}`);
        const { data }: { data: ClientFeatureConfig<T> } = response;
        return data;
    }

    public async createClientFeatureConfig<T extends FeatureInterfaceBase>(
        newClientFeatureConfig: NewClientFeatureConfig<T>,
    ): Promise<ClientFeatureConfig<T>> {
        const { clientId, config, featureId } = newClientFeatureConfig;
        const response: AxiosResponse = await HttpClient.post(`${this.#baseURL}/${clientId}/featureconfig`, {
            config,
            featureId,
        });
        const { data }: { data: ClientFeatureConfig<T> } = response;
        return data;
    }

    public async updateClientFeatureConfigByFeatureConfigId<T extends FeatureInterfaceBase>(
        clientId: number,
        clientFeatureConfigId: number,
        config: NewClientFeatureConfig<T>,
    ): Promise<ClientFeatureConfig<T>> {
        const response: AxiosResponse = await HttpClient.patch(
            `${this.#baseURL}/${clientId}/featureconfig/${clientFeatureConfigId}`,
            { config },
        );
        const { data }: { data: ClientFeatureConfig<T> } = response;
        return data;
    }

    public async upsertClientFeatureConfigByFeatureConfigId<T extends FeatureInterfaceBase>(
        clientId: number,
        config: any,
        featureId: number,
        clientFeatureConfigId?: number,
    ): Promise<ClientFeatureConfig<T>> {
        const url = clientFeatureConfigId
            ? `${this.#baseURL}/${clientId}/featureconfig/${clientFeatureConfigId}`
            : `${this.#baseURL}/${clientId}/featureconfig`;
        const response: AxiosResponse = await HttpClient.put(url, { featureId, config });
        const { data }: { data: ClientFeatureConfig<T> } = response;
        return data;
    }

    public async deleteClientFeatureConfigByFeatureId(clientId: number, featureId: number): Promise<void> {
        await HttpClient.post(`${this.#baseURL}/${clientId}/featureconfig/${featureId}`);
    }

    public async deleteClientFeatureConfigById(clientId: number, featureConfigId: number): Promise<void> {
        await HttpClient.delete(`${this.#baseURL}/${clientId}/featureconfig/${featureConfigId}`);
    }

    public async copyClientFeatureConfigToClients<T extends FeatureInterfaceBase>(
        clientId: number,
        configFeaturesToCopy: React.Key[],
        targettedClients: React.Key[],
    ): Promise<ClientFeatureConfig<T>[]> {
        const response: AxiosResponse = await HttpClient.post(
            `${this.#baseURL}/${clientId}/featureconfig/copyconfigs`,
            {
                configFeaturesToCopy,
                targettedClients,
            },
        );
        const { data }: { data: ClientFeatureConfig<T>[] } = response;
        return data;
    }
};

export default new ClientFeatureConfigService();
